import { useState } from 'react';

import { Box, Tab, Tabs, Dialog, Divider, IconButton, Typography, DialogTitle } from '@mui/material';

import { SHOW_NOTIFICATION_PREFERENCES } from 'src/feature-flags';

import { Iconify } from 'src/components/iconify';

import { ProfileTab } from './account-settings-profile-tab';
import { NotificationsTab } from './account-settings-notifications-tab';

// ----------------------------------------------------------------------
// Account Settings — tabbed dialog opened from the account popover. This is just the shell (frame,
// title, tabs); each tab lives in its own file and owns its state + footer:
//   • Profile      — account-settings-profile-tab.tsx (real user data, avatar, reset password)
//   • Notifications — account-settings-notifications-tab.tsx, shown only when
//     SHOW_NOTIFICATION_PREFERENCES is on (see src/feature-flags.ts)
// Colours come from the app theme (sx tokens) so it follows light/dark automatically.
// ----------------------------------------------------------------------

type Props = { open: boolean; onClose: () => void };

export function AccountSettingsDialog({ open, onClose }: Props) {
  const [tab, setTab] = useState<'profile' | 'notifications'>('profile');
  // The Profile tab's reset-password screen takes over the whole dialog; hide the tabs bar while it's up.
  const [resetActive, setResetActive] = useState(false);

  const handleClose = () => {
    onClose();
    setTab('profile');
    setResetActive(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { maxWidth: 720, borderRadius: '20px' } }}>
      <DialogTitle sx={{ pb: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Account Settings</Typography>
          <IconButton onClick={handleClose}>
            <Iconify icon="material-symbols:close-rounded" />
          </IconButton>
        </Box>
      </DialogTitle>

      {!resetActive && (
        <Box sx={{ px: 3 }}>
          <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ minHeight: 44 }}>
            <Tab value="profile" label="Profile" />
            {SHOW_NOTIFICATION_PREFERENCES && <Tab value="notifications" label="Notifications" />}
          </Tabs>
          <Divider />
        </Box>
      )}

      {SHOW_NOTIFICATION_PREFERENCES && tab === 'notifications' ? (
        <NotificationsTab onClose={handleClose} />
      ) : (
        <ProfileTab onClose={handleClose} onResetActiveChange={setResetActive} />
      )}
    </Dialog>
  );
}
